import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { 
  Check, 
  FileText, 
  Shield, 
  Zap, 
  ArrowRight, 
  Database, 
  Users, 
  Brain, 
  LayoutGrid, 
  Lock, 
  CheckCircle, 
  Smartphone, 
  Globe, 
  FileCheck, 
  Clock, 
  Activity, 
  AlertCircle, 
  ScanLine, 
  DollarSign, 
  TrendingUp, 
  BarChart, 
  Search,
  Sparkles,
  Layers,
  Settings,
  Server,
  Workflow
} from 'lucide-react';
import { Link } from 'react-router-dom';

const ClaimSubmissionPage = () => {
  const [activeTab, setActiveTab] = useState('accuracy');
  const [isAutoRotating, setIsAutoRotating] = useState(true);

  // Tab rotation logic
  useEffect(() => {
    let interval;
    if (isAutoRotating) {
      interval = setInterval(() => {
        setActiveTab((current) => {
          const tabs = ['accuracy', 'bandwidth', 'integration'];
          const currentIndex = tabs.indexOf(current);
          const nextIndex = (currentIndex + 1) % tabs.length;
          return tabs[nextIndex];
        });
      }, 5000);
    }
    return () => clearInterval(interval);
  }, [isAutoRotating]);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setIsAutoRotating(false);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .glass-card {
          background: rgba(255, 255, 255, 0.7);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.5);
          box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.07);
        }
        @keyframes progress {
          0% { width: 0%; }
          100% { width: 100%; }
        }
        .animate-progress {
          animation: progress 5s linear;
        }
        @keyframes flow-up {
          0% { transform: translateY(100%); opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { transform: translateY(-150%); opacity: 0; }
        }
        .animate-flow-up {
          animation: flow-up 3s linear infinite;
        }
        @keyframes flow-right {
          0% { transform: translateX(0); opacity: 0; }
          20% { opacity: 1; }
          80% { opacity: 1; }
          100% { transform: translateX(200px); opacity: 0; }
        }
        .animate-flow-right {
          animation: flow-right 3s linear infinite;
        }
        @keyframes pulse-glow {
          0%, 100% { box-shadow: 0 0 15px rgba(59, 130, 246, 0.3); }
          50% { box-shadow: 0 0 25px rgba(59, 130, 246, 0.6); }
        }
        .animate-pulse-glow {
          animation: pulse-glow 2s infinite;
        }
      `}</style>
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 relative overflow-hidden bg-slate-50">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-50 via-indigo-50/50 to-white"></div>
        
        <div className="container mx-auto max-w-7xl relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-sm text-blue-700 text-xs font-medium border border-blue-200/50 shadow-sm">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Meet Ariel, Your AI Claims Expert</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl font-bold leading-tight text-slate-900">
                Ariel, <br />
                <span className="text-blue-600">The AI Dental Biller</span>
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed max-w-lg">
                Our AI automatically generates claims using the patient and clinical data migrated into Asha PMS, then builds clean submissions with 99% accuracy.
              </p>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg font-semibold transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 shadow-blue-200">
                    Get a Demo
                  </Button>
                </Link>
                <Button 
                  variant="outline"
                  className="bg-white border-2 border-slate-200 hover:border-blue-600 hover:text-blue-600 px-8 py-6 rounded-xl text-lg font-semibold transition-all duration-300"
                >
                  View Pricing
                </Button>
              </div>
            </div>
            
            {/* Right: Interactive AI Visual */}
            <div className="relative flex items-center justify-center h-[500px]">
              <div className="relative">
                {/* Main Image Card */}
                <div className="relative p-1 rounded-3xl bg-gradient-to-br from-blue-300 via-blue-400 to-indigo-400 shadow-2xl">
                  <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-[22px] overflow-hidden w-72 h-96">
                    <img
                      src="/assets/ariel.png"
                      alt="Ariel AI Claim Submission"
                      className="w-full h-full object-cover opacity-95"
                    />
                  
                  {/* Glowing circle indicator */}
                  <div className="absolute top-32 left-12">
                    <div className="relative">
                      <div className="w-8 h-8 rounded-full bg-cyan-300 animate-pulse"></div>
                      <div className="absolute inset-0 w-8 h-8 rounded-full bg-cyan-200 animate-ping"></div>
                    </div>
                  </div>
                  
                  {/* Status at bottom */}
                  <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-sm rounded-2xl px-6 py-3 shadow-lg border border-white/50 w-max">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1.5">
                         <Database className="w-3.5 h-3.5 text-blue-500 animate-pulse" />
                         <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wide">Syncing</span>
                      </div>
                      <div className="w-px h-3 bg-slate-200"></div>
                      <div className="flex items-center gap-1.5">
                         <Search className="w-3.5 h-3.5 text-indigo-500 animate-pulse" />
                         <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wide">Analyzing</span>
                      </div>
                    </div>
                  </div>
                </div>
                </div>
                
                {/* Speech bubble */}
                <div className="absolute -top-4 -right-20 bg-white/95 backdrop-blur-xl rounded-2xl p-5 shadow-xl w-64 border border-white/50 animate-float">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                      <FileCheck className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-xs text-slate-600 leading-relaxed font-medium">
                        Claim D2740 verified. <br/>
                        <span className="text-green-600 font-bold">✓ Attachments found</span><br/>
                        <span className="text-green-600 font-bold">✓ Policy Checked</span>
                      </p>
                    </div>
                  </div>
                </div>
                
                {/* Stats card */}
                <div className="absolute bottom-20 -left-16 bg-white/95 backdrop-blur-xl rounded-2xl p-4 shadow-xl border border-white/50 animate-float" style={{ animationDelay: '1s' }}>
                  <div className="flex items-center gap-3">
                    <div className="relative w-10 h-10 flex items-center justify-center bg-green-100 rounded-full text-green-600">
                      <TrendingUp className="w-5 h-5" />
                    </div>
                    <div>
                      <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">First Pass Rate</p>
                      <p className="text-lg font-bold text-slate-900">98%</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Intelligent Scrubbing Section - Redesigned */}
      <section className="py-24 px-6 bg-white overflow-hidden border-b border-gray-50">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="space-y-8 relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium border border-blue-100 mb-4 uppercase tracking-wide">
                  Prevent Denials
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6 leading-tight">
                  Intelligent <span className="text-blue-600">AI Scrubbing</span>
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed">
                  We have an intelligent AI scrubbing system that analyzes patterns from hundreds of payers and also analyzes your historical claim history so that it can prevent denials up front.
                </p>
                <p className="text-lg text-slate-600 leading-relaxed mt-4">
                  Ariel catches errors before they leave your office, ensuring your claims are clean and compliant every single time.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Link to="/book-demo">
                  <Button className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 rounded-xl text-lg font-semibold transition-all duration-300 shadow-lg hover:-translate-y-0.5">
                    See It In Action
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Visual - Dashboard Style */}
            <div className="relative">
              {/* Background Glow */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-blue-50 rounded-full blur-3xl -z-10"></div>
              
              <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden">
                {/* Header */}
                <div className="bg-slate-50/80 backdrop-blur border-b border-slate-100 p-4 flex items-center justify-between">
                   <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-400"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                      <div className="w-3 h-3 rounded-full bg-green-400"></div>
                   </div>
                   <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Smart Scrubber v2.0</div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4">
                   {/* Item 1: Correction */}
                   <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 relative overflow-hidden group">
                      <div className="absolute top-0 left-0 w-1 h-full bg-amber-400"></div>
                      <div className="flex justify-between items-start mb-2">
                         <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-900">Claim #4829</span>
                            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">D2740</span>
                         </div>
                         <span className="text-xs font-bold text-amber-500 bg-amber-50 px-2 py-1 rounded-full">Action Needed</span>
                      </div>
                      <p className="text-xs text-slate-600 mb-3">Missing required X-ray attachment for Delta Dental policy.</p>
                      
                      {/* AI Action */}
                      <div className="bg-slate-50 rounded-lg p-3 flex items-center justify-between border border-slate-100">
                         <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center">
                               <Sparkles className="w-3 h-3 text-blue-600" />
                            </div>
                            <span className="text-xs font-medium text-slate-700">AI found matching image: <span className="text-blue-600">PA_Bitewing.jpg</span></span>
                         </div>
                         <Button size="sm" variant="ghost" className="h-7 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50">
                            Auto-Attach
                         </Button>
                      </div>
                   </div>

                   {/* Item 2: Success */}
                   <div className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 relative overflow-hidden opacity-90">
                      <div className="absolute top-0 left-0 w-1 h-full bg-green-500"></div>
                      <div className="flex justify-between items-start mb-1">
                         <div className="flex items-center gap-2">
                            <span className="text-sm font-bold text-slate-900">Claim #4830</span>
                            <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">D1110</span>
                         </div>
                         <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded-full">Verified</span>
                      </div>
                      <p className="text-xs text-slate-500">Passed all historical acceptance checks.</p>
                   </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The Ariel Advantage - Tabbed Section */}
      <section className="py-24 px-6 bg-slate-50">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center mb-16 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              The Ariel Advantage
            </h2>
            <p className="text-lg text-slate-600">
              Why leading practices trust Ariel to handle their revenue cycle.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left: Interactive Tabs */}
            <div className="space-y-4">
              {[
                {
                  id: 'accuracy',
                  icon: ScanLine,
                  title: 'Greater Accuracy',
                  desc: 'Cross-references policies and thousands of data points to ensure every claim is accurate before submission.'
                },
                {
                  id: 'bandwidth',
                  icon: Zap,
                  title: 'Unlimited Bandwidth',
                  desc: 'Process thousands of claims instantly. Never fall behind on your A/R again, regardless of staff availability.'
                },
                {
                  id: 'integration',
                  icon: Database,
                  title: 'Migrated Clinical Data',
                  desc: 'Works from the clinical notes, x-rays, and perio charts moved into Asha PMS without manual re-entry.'
                }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabClick(tab.id)}
                  className={`w-full text-left p-6 rounded-2xl transition-all duration-300 border relative overflow-hidden group ${
                    activeTab === tab.id
                      ? 'bg-white border-blue-200 shadow-lg ring-1 ring-blue-50'
                      : 'bg-white border-transparent hover:border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {activeTab === tab.id && isAutoRotating && (
                    <div className="absolute bottom-0 left-0 h-1 bg-blue-50 w-full">
                      <div className="h-full bg-blue-600 animate-progress"></div>
                    </div>
                  )}
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-xl transition-colors ${
                      activeTab === tab.id ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-500'
                    }`}>
                      <tab.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className={`text-lg font-bold mb-2 ${
                        activeTab === tab.id ? 'text-slate-900' : 'text-slate-600'
                      }`}>
                        {tab.title}
                      </h3>
                      <p className={`text-sm leading-relaxed ${
                        activeTab === tab.id ? 'text-slate-600' : 'text-slate-400'
                      }`}>
                        {tab.desc}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Right: Dynamic Visual */}
            <div className="relative h-[500px] bg-white rounded-3xl shadow-xl border border-slate-100 overflow-hidden p-8 flex items-center justify-center">
              <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-white"></div>
              
              {/* Visual for Accuracy */}
              <div className={`transition-all duration-500 absolute inset-0 flex items-center justify-center p-8 ${activeTab === 'accuracy' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10 pointer-events-none'}`}>
                 <div className="w-full max-w-sm bg-white rounded-xl shadow-lg border border-slate-100 p-6 space-y-6">
                    <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                       <h4 className="font-bold text-slate-700">Policy Check</h4>
                       <span className="text-green-600 font-bold text-xs bg-green-50 px-2 py-1 rounded-full">99.9% Match</span>
                    </div>
                    <div className="space-y-3">
                       <div className="flex items-center gap-3 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Code D2740 Validated</span>
                       </div>
                       <div className="flex items-center gap-3 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Frequency Checked (5 years)</span>
                       </div>
                       <div className="flex items-center gap-3 text-sm text-slate-600">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span>Required Attachments Present</span>
                       </div>
                    </div>
                 </div>
              </div>

              {/* Visual for Bandwidth - IMPROVED: Parallel Processing Stream */}
              <div className={`transition-all duration-500 absolute inset-0 flex items-center justify-center p-8 ${activeTab === 'bandwidth' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10 pointer-events-none'}`}>
                 <div className="w-full h-full relative flex flex-col items-center justify-center">
                    
                    {/* Top Gauge */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur rounded-full px-6 py-2 shadow-lg border border-slate-100 z-10 flex items-center gap-3">
                        <div className="flex flex-col items-center">
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Throughput</span>
                            <span className="text-xl font-bold text-blue-600">240/min</span>
                        </div>
                        <div className="w-px h-8 bg-slate-200"></div>
                         <div className="flex flex-col items-center">
                            <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Queue</span>
                            <span className="text-xl font-bold text-green-500">0</span>
                        </div>
                    </div>

                    {/* Processing Lanes */}
                    <div className="flex gap-4 h-full w-full justify-center pt-16">
                        {[0, 1, 2].map((lane) => (
                            <div key={lane} className="w-24 h-full bg-slate-50/50 rounded-2xl border border-slate-100 relative overflow-hidden flex justify-center">
                                {/* Grid Lines */}
                                <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(to bottom, transparent 49%, #e2e8f0 50%)', backgroundSize: '100% 20px' }}></div>
                                
                                {/* Moving Cards */}
                                {[0, 1, 2].map((card) => (
                                    <div 
                                        key={card}
                                        className="absolute w-16 h-20 bg-white rounded-lg shadow-md border border-blue-100 flex flex-col items-center justify-center gap-1 animate-flow-up"
                                        style={{ 
                                            animationDelay: `${(lane * 0.5) + (card * 1.5)}s`,
                                            opacity: 0
                                        }}
                                    >
                                        <FileText className="w-6 h-6 text-blue-500" />
                                        <div className="w-8 h-1 bg-slate-100 rounded-full"></div>
                                        <div className="w-6 h-1 bg-slate-100 rounded-full"></div>
                                    </div>
                                ))}
                                
                                {/* Bottom Blur */}
                                <div className="absolute bottom-0 w-full h-20 bg-gradient-to-t from-white to-transparent z-10"></div>
                                {/* Top Blur */}
                                <div className="absolute top-0 w-full h-20 bg-gradient-to-b from-white to-transparent z-10"></div>
                            </div>
                        ))}
                    </div>

                    {/* Floating Label */}
                    <div className="absolute bottom-10 bg-blue-600 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg shadow-blue-200 animate-pulse-glow">
                        Parallel Processing Active
                    </div>
                 </div>
              </div>

              {/* Visual for Integration - IMPROVED: Deep Pipeline Connection */}
              <div className={`transition-all duration-500 absolute inset-0 flex items-center justify-center p-8 ${activeTab === 'integration' ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10 pointer-events-none'}`}>
                 <div className="w-full h-full relative flex items-center justify-between px-4">
                    
                    {/* Left Side: PMS */}
                    <div className="w-24 h-32 bg-slate-800 rounded-xl shadow-xl flex flex-col items-center justify-center relative z-10 border-b-4 border-slate-600">
                        <Database className="w-8 h-8 text-slate-300 mb-2" />
                        <span className="text-xs font-bold text-slate-300">Your PMS</span>
                        {/* Scanning Effect */}
                        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-blue-500/20 to-transparent h-1/2 w-full animate-flow-up" style={{ animationDuration: '2s' }}></div>
                    </div>

                    {/* Connection Line */}
                    <div className="flex-1 h-2 bg-slate-100 relative overflow-hidden rounded-full mx-4">
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-200 to-transparent w-1/2 animate-flow-right"></div>
                    </div>

                    {/* Right Side: Ariel */}
                    <div className="w-24 h-32 bg-blue-600 rounded-xl shadow-xl flex flex-col items-center justify-center relative z-10 border-b-4 border-blue-800 animate-pulse-glow">
                        <Sparkles className="w-8 h-8 text-white mb-2" />
                        <span className="text-xs font-bold text-white">Ariel</span>
                    </div>

                    {/* Floating Data Packets moving Left to Right */}
                    <div className="absolute inset-0 pointer-events-none">
                        {[
                            { icon: FileText, label: 'Notes', delay: '0s', top: '30%' },
                            { icon: ScanLine, label: 'X-Rays', delay: '1s', top: '50%' },
                            { icon: Activity, label: 'Perio', delay: '2s', top: '70%' },
                        ].map((item, i) => (
                            <div 
                                key={i}
                                className="absolute left-28 bg-white p-2 rounded-lg shadow-md border border-blue-100 flex items-center gap-2 animate-flow-right"
                                style={{ 
                                    top: item.top,
                                    animationDelay: item.delay,
                                    animationDuration: '2.5s'
                                }}
                            >
                                <item.icon className="w-4 h-4 text-blue-500" />
                                <span className="text-xs font-bold text-slate-700">{item.label}</span>
                            </div>
                        ))}
                    </div>

                 </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* Slip Through Cracks Section - Updated Visual */}
      <section className="py-24 px-6 bg-white">
        <div className="container mx-auto max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left: Content */}
            <div className="order-2 lg:order-1">
               <div className="relative bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
                  <h3 className="font-bold text-slate-900 mb-6 text-lg">Revenue Pipeline</h3>
                  
                  <div className="space-y-6">
                     {/* Step 1 */}
                     <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600 font-bold">1</div>
                        <div className="flex-1 bg-slate-50 p-3 rounded-lg border border-slate-100">
                           <div className="flex justify-between mb-1">
                              <span className="text-sm font-bold text-slate-700">Treatment Completed</span>
                              <span className="text-xs text-slate-400">10:00 AM</span>
                           </div>
                           <div className="w-full bg-slate-200 h-1.5 rounded-full">
                              <div className="bg-blue-600 h-1.5 rounded-full w-full"></div>
                           </div>
                        </div>
                     </div>
                     
                     {/* Connector */}
                     <div className="h-6 w-0.5 bg-slate-200 ml-5 -my-3"></div>

                     {/* Step 2 */}
                     <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600 font-bold">2</div>
                        <div className="flex-1 bg-slate-50 p-3 rounded-lg border border-slate-100">
                           <div className="flex justify-between mb-1">
                              <span className="text-sm font-bold text-slate-700">Claim Generated & Scrubbed</span>
                              <span className="text-xs text-slate-400">10:01 AM</span>
                           </div>
                           <div className="w-full bg-slate-200 h-1.5 rounded-full">
                              <div className="bg-blue-600 h-1.5 rounded-full w-full"></div>
                           </div>
                        </div>
                     </div>

                     {/* Connector */}
                     <div className="h-6 w-0.5 bg-slate-200 ml-5 -my-3"></div>

                     {/* Step 3 */}
                     <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 text-green-600 font-bold">
                           <Check className="w-5 h-5" />
                        </div>
                        <div className="flex-1 bg-green-50 p-3 rounded-lg border border-green-100">
                           <div className="flex justify-between mb-1">
                              <span className="text-sm font-bold text-green-800">Submitted to Payer</span>
                              <span className="text-xs text-green-600">10:02 AM</span>
                           </div>
                           <p className="text-xs text-green-700">Zero lag time. Maximum reimbursement speed.</p>
                        </div>
                     </div>
                  </div>
               </div>
            </div>

            {/* Right: Text */}
            <div className="order-1 lg:order-2 space-y-6">
              <p className="text-xs text-blue-600 font-bold uppercase tracking-wider">MAXIMIZE REVENUE</p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
                Never Let a Claim <br/>
                <span className="text-blue-600">Slip Through the Cracks</span>
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed">
                Manual claim submission is prone to human error and delays. Ariel operates 24/7, ensuring every completed procedure is billed immediately and accurately.
              </p>
              <ul className="space-y-4 pt-4">
                {[
                  'Increase First Pass Acceptance',
                  'Reduce Days in A/R',
                  'Free Up Your Team'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-slate-700 font-medium">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-600 to-blue-700 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />
          </svg>
        </div>

        <div className="container mx-auto max-w-4xl text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to automate your claim submission?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join the practices that have streamlined their billing, reduced overhead, and improved cash flow with Ariel.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book-demo">
              <Button className="bg-white text-blue-600 hover:bg-blue-50 px-10 py-7 rounded-full text-lg font-semibold transition-all duration-300 hover:scale-105 shadow-2xl w-full sm:w-auto">
                Book a Demo
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ClaimSubmissionPage;
